import { useEffect, useState } from "react";
import { api, resolveMedia } from "../lib/api";
import { openWhatsApp } from "../lib/whatsapp";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { InkFlourish, InkSailboat, InkOcote, InkPaloma } from "../components/Ink";

const ZONES = ["Avándaro", "Valle Centro", "La Peña", "Rancho San Gaspar", "Otra"];

const FALLBACK_HERO = "https://images.unsplash.com/photo-1493809842364-78817add7ffb?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";
const FALLBACK_STRIP = [
  "https://images.unsplash.com/photo-1436076863939-06870fe779c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1568213816046-0ee1c42bd559?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1568254183919-78a4f43a2877?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

export default function Concierge() {
  const [form, setForm] = useState({ name: "", contact: "", zone: "", arrival: "", departure: "", notes: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hero, setHero] = useState(FALLBACK_HERO);
  const [strip, setStrip] = useState(FALLBACK_STRIP);

  useEffect(() => {
    api.get("/site/concierge").then((r) => {
      if (r.data?.hero) setHero(r.data.hero);
      if (Array.isArray(r.data?.strip) && r.data.strip.length === 3) setStrip(r.data.strip);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/concierge", form);
      setSent(true);
      toast.success("Recibimos su solicitud. Le escribimos hoy mismo.");
    } catch {
      toast.error("Algo falló. Intente de nuevo, por favor.");
    } finally { setLoading(false); }
  };

  const chat = () => openWhatsApp(`Hola. Soy ${form.name || "un futuro huésped"}. Me gustaría coordinar mi próximo fin en el Valle.`);

  return (
    <div data-testid="concierge-page" className="bg-white">
      {/* HERO — Cobalt canvas full-bleed */}
      <section className="canvas-cobalt py-20 sm:py-28 relative overflow-hidden">
        <InkPaloma className="hidden lg:block absolute top-14 left-16 w-40 h-24 opacity-50" stroke="#E88A5A" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 text-white order-2 lg:order-1">
            <p className="eyebrow-on-dark">Servicio de Concierge</p>
            <h1 className="mt-5 font-serif text-5xl sm:text-7xl lg:text-8xl leading-[0.98]">Considérelo <span className="serif-italic" style={{ color: "#E88A5A" }}>hecho.</span></h1>
            <p className="script-cream text-3xl sm:text-4xl mt-6" style={{ color: "#E88A5A" }}>antes de que salga de la ciudad</p>
            <InkFlourish className="w-48 h-6 mt-8 opacity-70" stroke="#FFFFFF" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 overflow-hidden">
            <img src={resolveMedia(hero)} alt="Cabaña con chimenea" className="w-full h-[52vh] object-cover" />
          </div>
        </div>
      </section>

      {/* LETTER — white */}
      <section className="bg-white max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-24 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="script text-5xl leading-none">una carta breve</p>
            <InkFlourish className="w-32 h-4 mt-8 opacity-60" />
          </div>
          <div className="md:col-span-8 prose-editorial">
            <p>Ustedes ya conocen el Valle. Saben qué panadería les gusta, en qué muelle guardan el velero, dónde compra el café su cocinera. Lo que a veces falta es tiempo — y la voluntad de dedicarle un jueves entero a coordinarlo todo antes de subirse al coche el viernes.</p>
            <p>Nosotros lo hacemos por ustedes. Con discreción, sin correos innecesarios, sin apps que descargar. Un WhatsApp y un formulario corto: lo demás lo resolvemos.</p>
            <p>Encontramos la casa lista cuando llegan. La leña, cortada. El pan, sobre la mesa. La reservación del sábado, confirmada al horario que prefieren. El masajista del domingo, en su casa a la hora que nos digan. Si el fin cambia de plan, cambiamos el plan con ustedes.</p>
            <p className="serif-italic text-cobalt-deep mt-8 text-lg">— El equipo del Insider</p>
          </div>
        </div>
      </section>

      {/* TRIPTYCH — Cream canvas */}
      <section className="canvas-cream py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow">La Casa Lista</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-cobalt-deep">Todo puesto <span className="serif-italic text-rust">antes de que lleguen.</span></h2>
          </div>
          <div className="grid grid-cols-3 gap-4 sm:gap-8">
            {[
              { src: strip[0], label: "el almuerzo" },
              { src: strip[1], label: "la cava" },
              { src: strip[2], label: "el pan" },
            ].map((it, i) => (
              <div key={i} className="fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="overflow-hidden aspect-[3/4]">
                  <img src={resolveMedia(it.src)} alt="" className="card-img w-full h-full object-cover" />
                </div>
                <p className="script text-3xl mt-4 leading-none">{it.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE PILLARS — Olive canvas */}
      <section className="canvas-olive py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4 text-white">
            <p className="script-cream text-5xl leading-none">qué resolvemos</p>
            <p className="mt-6 font-serif text-2xl leading-snug max-w-sm">Lo previsible, lo especial y todo lo intermedio.</p>
            <InkOcote className="w-20 h-32 mt-10 opacity-90" stroke="#FFFFFF" />
          </div>
          <div className="md:col-span-8 text-white">
            <ul className="divide-y divide-white/30">
              {[
                { t: "Abastecimiento de casa", d: "Despensa, cava, flores frescas, leña. Compramos donde ustedes ya compran." },
                { t: "Reservaciones prioritarias", d: "Mesas, spas, clases, embarcaciones. Coordinadas al horario exacto." },
                { t: "Personal verificado", d: "Cocineros, meseros, masajistas y choferes de nuestra red discreta." },
                { t: "Itinerarios a la medida", d: "Un plan corto de fin de semana, escrito para su casa y sus tiempos." },
              ].map((s, i) => (
                <li key={i} className="py-6 flex gap-6">
                  <p className="font-serif text-3xl shrink-0 leading-none w-14">0{i + 1}</p>
                  <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                    <h3 className="font-serif text-xl">{s.t}</h3>
                    <p className="sm:max-w-md text-sm text-white/85 leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL — white */}
      <section className="bg-white max-w-4xl mx-auto px-6 sm:px-10 py-28 text-center">
        <p className="eyebrow">Palabra de Miembros</p>
        <blockquote className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug text-ink">
          <span className="serif-italic">"Llegamos el viernes a las nueve. La casa olía a leña, el pan estaba tibio y la reservación del sábado ya estaba en mi celular. No tuve que abrir una sola app."</span>
        </blockquote>
        <p className="mt-8 font-display text-[0.68rem] tracking-[0.28em] uppercase text-cobalt font-semibold">— M. B., miembro desde 2023, Avándaro</p>
        <InkFlourish className="w-48 h-6 mx-auto mt-10 opacity-60" />
      </section>

      {/* FORM — Rust canvas */}
      <section className="canvas-rust py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 text-white">
            <p className="script-cream text-5xl leading-none">solicitud discreta</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight">Cuéntenos lo básico. <span className="serif-italic">Nosotros preguntamos el resto.</span></h2>
            <p className="mt-5 text-white/85 leading-relaxed max-w-md">Respondemos el mismo día por WhatsApp o por el medio que prefiera. Sin cargo por cotización.</p>
            <InkSailboat className="w-32 h-32 mt-10 opacity-80" stroke="#FFFFFF" />
          </div>
          <div className="lg:col-span-7">
            {sent ? (
              <div className="bg-white border border-white p-10" data-testid="concierge-success">
                <Check size={28} className="text-cobalt" />
                <h3 className="mt-5 font-serif text-2xl text-cobalt-deep">Su solicitud está con nosotros.</h3>
                <p className="mt-3 text-muted2 leading-relaxed">Le escribimos hoy mismo con las primeras opciones. Si prefiere adelantar la conversación, puede abrir WhatsApp aquí.</p>
                <button className="btn-primary mt-8" onClick={chat} data-testid="concierge-whatsapp">Abrir WhatsApp</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-7" data-testid="concierge-form">
                <Field label="Nombre completo" required>
                  <input required data-testid="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b border-white py-3 text-white placeholder-white/50 focus:border-white" />
                </Field>
                <Field label="WhatsApp o correo" required>
                  <input required data-testid="c-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="w-full bg-transparent border-b border-white py-3 text-white placeholder-white/50 focus:border-white" />
                </Field>
                <Field label="Zona de la propiedad">
                  <select data-testid="c-zone" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className="w-full bg-transparent border-b border-white py-3 text-white focus:border-white">
                    <option value="" className="text-ink">Selecciona…</option>
                    {ZONES.map((z) => <option key={z} className="text-ink">{z}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-6">
                  <Field label="Llegada">
                    <input type="date" data-testid="c-arrival" value={form.arrival} onChange={(e) => setForm({ ...form, arrival: e.target.value })} className="w-full bg-transparent border-b border-white py-3 text-white focus:border-white" />
                  </Field>
                  <Field label="Salida">
                    <input type="date" data-testid="c-departure" value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value })} className="w-full bg-transparent border-b border-white py-3 text-white focus:border-white" />
                  </Field>
                </div>
                <Field label="Notas — qué le gustaría dejar resuelto">
                  <textarea rows={4} data-testid="c-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-transparent border-b border-white py-3 text-white placeholder-white/50 focus:border-white resize-none" placeholder="Pan de La Panadería, masajista sábado 10:00, reservar Santo Coyote…" />
                </Field>
                <button disabled={loading} className="btn-outline-cream" data-testid="c-submit">{loading ? "Enviando…" : "Enviar solicitud"}</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="eyebrow-on-dark">{label}{required && " ·"}</span>
    <div className="mt-2">{children}</div>
  </label>
);
